import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow,
    TableHead, Paper, Button, TextField, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, Box, Typography, ButtonGroup
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const MODOS = {
    lados: {
        titulo: 'Lados',
        endpoint: 'ImgLados',
        campoDescripcion: 'descripcion',
        campoId: 'idLado',
        permisoCrear: 'Crear Lado',
        permisoEditar: 'Editar Lado',
        permisoEliminar: 'Eliminar Lado',
    },
    tiposentrega: {
        titulo: 'Tipos de Entrega',
        endpoint: 'ImgTiposDeEntrega',
        campoDescripcion: 'descripcion',
        campoId: 'idTipoEntrega',
        permisoCrear: 'Crear Tipo Entrega',
        permisoEditar: 'Editar Tipo Entrega',
        permisoEliminar: 'Eliminar Tipo Entrega',
    }
};

function ConfigCatalogos({ modo: modoProp }) {
    const [searchParams] = useSearchParams();
    const modoKey = modoProp || searchParams.get('modo') || 'lados';
    const config = MODOS[modoKey] || MODOS.lados;

    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;

    const [items, setItems] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formData, setFormData] = useState({ descripcion: '' });
    const [loading, setLoading] = useState(false);

    const fetchItems = async () => {
        try {
            const response = await fetch(`${server}/${config.endpoint}`);
            const data = await response.json();
            setItems(data.$values || data || []);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'Error al cargar datos', 'error');
        }
    };

    useEffect(() => {
        fetchItems();
        setEditingItem(null);
        setFormData({ descripcion: '' });
        setPage(0);
    }, [modoKey]);

    useEffect(() => {
        if (editingItem) {
            setFormData({ descripcion: editingItem[config.campoDescripcion] || '' });
        } else {
            setFormData({ descripcion: '' });
        }
    }, [editingItem]);

    const handleSave = async () => {
        if (!formData.descripcion.trim()) {
            Swal.fire('Validación', 'La descripción es requerida', 'warning');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                [config.campoDescripcion]: formData.descripcion,
                ingresadoPor: user.codigoUsuario.padEnd(20),
                modificadoPor: user.codigoUsuario.padEnd(20)
            };
            const method = editingItem ? 'PUT' : 'POST';
            const url = editingItem
                ? `${server}/${config.endpoint}/${editingItem[config.campoId]}`
                : `${server}/${config.endpoint}`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                Swal.fire('Éxito', editingItem ? 'Actualizado correctamente' : 'Creado correctamente', 'success');
                setEditingItem(null);
                setFormData({ descripcion: '' });
                fetchItems();
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al guardar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            const username = user.codigoUsuario.padEnd(20) + '/';
            const response = await fetch(
                `${server}/${config.endpoint}/${itemToDelete[config.campoId]}/${username}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                setOpenDialog(false);
                setItemToDelete(null);
                fetchItems();
                Swal.fire('Éxito', 'Eliminado correctamente', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al eliminar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const filteredItems = items.filter(item =>
        item[config.campoDescripcion]?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Catálogo: {config.titulo}</h2>
            </div>

            {/* Formulario */}
            <Box sx={{ padding: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                <Typography variant="h6">
                    {editingItem ? `Editar ${config.titulo}` : `Nuevo ${config.titulo}`}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Descripción"
                            value={formData.descripcion}
                            onChange={(e) => setFormData({ descripcion: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ButtonGroup>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={loading || !tienePermiso(editingItem ? config.permisoEditar : config.permisoCrear)}
                            >
                                {loading ? 'Guardando...' : 'Guardar'}
                            </Button>
                            {editingItem && (
                                <Button
                                    variant="outlined"
                                    onClick={() => { setEditingItem(null); setFormData({ descripcion: '' }); }}
                                >
                                    Cancelar
                                </Button>
                            )}
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </Box>

            <div className="ln_solid" />
            <h3>Listado Existente</h3>

            <TextField
                label="Buscar"
                fullWidth
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ marginBottom: '20px' }}
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Descripción</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                            <TableRow key={item[config.campoId]}>
                                <TableCell>{item[config.campoDescripcion]}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        onClick={() => setEditingItem(item)}
                                        disabled={!tienePermiso(config.permisoEditar)}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" /> Editar
                                    </Button>
                                    <Button
                                        onClick={() => { setItemToDelete(item); setOpenDialog(true); }}
                                        disabled={!tienePermiso(config.permisoEliminar)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" /> Eliminar
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredItems.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    ¿Está seguro de eliminar "{itemToDelete?.[config.campoDescripcion]}"?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error">Eliminar</Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
    );
}

export default ConfigCatalogos;
